import React, { useContext, useEffect, useState } from "react";
import {
    TextField,
    Typography,
    IconButton,
    Box,
    Stack,
    Container,
    styled,
    Paper,
    InputAdornment
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Label from "../../commons/Label";
import service from "../service";
import { useDispatch } from "react-redux";
import { clearIndexedDb } from "../../../utilities/commonUtilities";
import * as staticConfigs from '../../../utilities/staticConfigs';
import LoadingContext from "../../../container/loadingContext";
import Notify from "../../commons/notify";
import { useHistory, useLocation } from 'react-router-dom';
import headerComponentService from '../../mainLayoutComponent/headerComponent/service';
import i18n from "../../../utilities/i18n";
import { getStorage } from "../../../utilities/browserStorage";
import CommonButton from "../../commons/Buttons";

const StyledCard = styled(Paper)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 1.5,
    overflow: 'hidden',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
    backgroundColor: '#ffffff',
}));

const StyledHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.button?.primary || '#1A335E',
    padding: theme.spacing(1.5),
    color: '#ffffff',
    textAlign: 'center',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        '& fieldset': {
            borderColor: '#D1D5DB',
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiInputBase-input::placeholder': {
        color: '#9CA3AF',
        opacity: 1,
    }
}));

const RequirementItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'passed',
})(({ theme, passed }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    transition: 'all 0.3s ease',
    '& .MuiSvgIcon-root': {
        fontSize: 18,
        marginRight: theme.spacing(1),
        color: passed ? '#4CAF50' : '#D1D5DB',
    },
    '& .MuiTypography-root': {
        color: passed ? theme.palette.text.primary : '#9CA3AF',
        fontFamily: 'Lato, sans-serif',
        fontSize: '0.85rem'
    }
}));

const Requirement = ({ passed, children }) => (
    <RequirementItem passed={passed}>
        {passed ? <CheckCircleIcon /> : <CancelIcon />}
        <Typography variant="body2">{children}</Typography>
    </RequirementItem>
);

const PasswordChangeForm = ({ type, header }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();
    const setShowLoadingBar = useContext(LoadingContext);

    const practicePK = getStorage('practice');
    const loginRoute = staticConfigs.LOGIN_ROUTE;
    const params = new URLSearchParams(location.search);
    const fToken = params.get('f_token');
    const userId = params.get('user_id');

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showNotify, setShowNotify] = useState("hide");
    const [notifyDescription, setNotifyDescription] = useState("");
    const [notifyType, setNotifyType] = useState("success");
    const [loginRedirect, setLoginRedirect] = useState(false);

    useEffect(() => {
        if (loginRedirect) {
            history.replace(loginRoute);
        }
    }, [loginRedirect, history, loginRoute]);

    const hasMinLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) && !/[{}\\/]/.test(newPassword);
    const passwordsMatch = newPassword === confirmPassword && confirmPassword !== "";

    function createFormData(data) {
        const formData = new FormData();
        for (const key in data) { formData.append(key, data[key]); }
        return formData;
    }

    const resetAllReduxStates = () => {
        dispatch({ type: 'chargesDebit/onResetData' });
        dispatch({ type: 'paymentAdjustment/onResetData' });
        dispatch({ type: 'openArReport/onResetData' });
        dispatch({ type: 'reportFilters/reset' });
        dispatch({ type: 'transactionReport/onResetData' });
        dispatch({ type: 'statementMgmnt/resetData' });
        dispatch({ type: 'patientInfo/setInitialState' });
        dispatch({ type: 'claimManagement/resetClaimtoInitalState' });
        dispatch({ type: 'claimSaveManagement/setClaimSavetoInitialState' });
        dispatch({ type: 'arClaims/ResetData' });
        dispatch({ type: 'ClaimFilter/reset' });
        dispatch({ type: 'batchEligibility/onResetData' });
        dispatch({ type: 'chatbox/reset' });
        dispatch({ type: 'commonStates/reset' });
        dispatch({ type: 'claimManagement/resetToInitial' });
        dispatch({ type: 'DropDownOptionSlice/resetToInitial' });
    };

    function showNotifyWindow(action, type, desc, age = 3000) {
        if (action === "show") {
            setTimeout(() => setShowNotify("hide"), age);
        }
        setShowNotify(action);
        setNotifyType(type);
        setNotifyDescription(desc);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowLoadingBar(true);
        if (!currentPassword && type !== "forgot") {
            setShowLoadingBar(false);
            showNotifyWindow('show', 'danger', "Please enter your current password");
            return;
        }
        if (!hasMinLength || !hasUpperCase || !hasNumber || !hasSpecialChar) {
            setShowLoadingBar(false);
            showNotifyWindow('show', 'danger', "Password requirements not met");
            return;
        }
        if (!passwordsMatch) {
            setShowLoadingBar(false);
            showNotifyWindow('show', 'danger', "Passwords do not match");
            return;
        }

        if (type === 'forgot') {
            const data = createFormData({ fToken, userId, new_password: newPassword, confirm_password: confirmPassword });
            service.ResetPassword(data).then(async (response) => {
                if (response.status === 200) {
                    showNotifyWindow('show', 'danger', "Passwords updated successfully");
                    localStorage.clear();
                    await clearIndexedDb();
                    resetAllReduxStates();
                    history.push(staticConfigs.LOGIN_ROUTE);
                } else {
                    handleErrorResponse(response);
                }
                setShowLoadingBar(false);
            });
        } else if (type === 'reset') {
            const data = createFormData({ 
                new_password: newPassword, 
                confirm_password: confirmPassword, 
                old_password: currentPassword,
                ...(practicePK && { 'practice_pk': practicePK })
            });
            service.ChangePassword(data).then(async (response) => {
                if (response.status === 200) {
                    showNotifyWindow('show', 'success', i18n.t('errorMessages.password_updated'));
                    setTimeout(() => headerComponentService.LogOut().then(() => setLoginRedirect(true)), 1000);
                    await clearIndexedDb();
                } else {
                    handleErrorResponse(response);
                }
                setShowLoadingBar(false);
            });
        } else if (type === 'force-reset') {
            const data = createFormData({
                'userId': getStorage('userID'),
                'old_password': currentPassword,
                'new_password': newPassword,
                'confirm_password': confirmPassword
            });
            service.ForceChangePassword(data).then(async (response) => {
                if (response.status === 200) {
                    showNotifyWindow('show', 'success', i18n.t('errorMessages.password_updated'));
                    localStorage.removeItem("passwordExpired");
                    localStorage.removeItem("userID");
                    await clearIndexedDb();
                    setLoginRedirect(true);
                } else {
                    handleErrorResponse(response);
                }
                setShowLoadingBar(false);
            });
        }
        setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    };

    const handleErrorResponse = (response) => {
        const msg = response.data.message;
        if (msg === 'old_password') showNotifyWindow('show', 'danger', i18n.t('errorMessages.password_old_error'));
        else if (msg === 'new_password') showNotifyWindow('show', 'danger', i18n.t('errorMessages.password_matching_error'));
        else if (msg === 'oldnew_same_password') showNotifyWindow('show', 'danger', i18n.t('errorMessages.password_oldnew_same_error'));
    };

    return (
        <Container maxWidth={false} sx={{ maxWidth: 420, mt: 6 }}>
            <Notify 
                showNotify={showNotify} 
                setShowNotify={setShowNotify} 
                notifyDescription={notifyDescription} 
                notifyType={notifyType} 
            />
            
            <StyledCard elevation={0}>
                <StyledHeader>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
                        {header || "Change Password"}
                    </Typography>
                </StyledHeader>

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        {(type === 'reset' || type === 'force-reset') && (
                            <Box>
                                <Label label="Current Password" fontWeight="600" color="#374151" />
                                <StyledTextField
                                    name="current_password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    fullWidth
                                    margin="dense"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                                                    {showCurrentPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        )}

                        <Box>
                            <Label label="New Password" fontWeight="600" color="#374151" />
                            <StyledTextField
                                name="new_password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter your password"
                                fullWidth
                                margin="dense"
                                type={showNewPassword ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                                {showNewPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{ py: 1 }}>
                            <Requirement passed={hasMinLength}>At least 8 characters</Requirement>
                            <Requirement passed={hasUpperCase}>At least one uppercase letter</Requirement>
                            <Requirement passed={hasNumber}>At least one number</Requirement>
                            <Requirement passed={hasSpecialChar}>At least one special character</Requirement>
                        </Box>

                        <Box>
                            <Label label="Confirm Password" fontWeight="600" color="#374151" />
                            <StyledTextField
                                name="confirm_password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Enter your password"
                                fullWidth
                                margin="dense"
                                type={showConfirmPassword ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                {showConfirmPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <CommonButton
                            type="submit"
                            variant="contained"
                            label={i18n.t("commons.save")}
                            fullWidth
                            sx={{ mt: 2, height: 48, borderRadius: '6px', textTransform: 'none', fontSize: '1rem' }}
                        />
                    </Stack>
                </Box>
            </StyledCard>
        </Container>
    );
};

export default PasswordChangeForm;